/**
 * T155P4 — RuleApplyBanner
 *
 * Shown at the top of the uncategorized bucket when active automation rules
 * have potential matches. Auto-triggers a dryRun on mount (once per session).
 *
 * Session-guard fix (2026-07-31): the marker is only committed after the dryRun
 * fulfils. A rejected request removes the marker so future mounts can retry, and
 * an accessible error/retry state is shown instead of silently returning null.
 *
 * data-testid="rule-apply-banner"
 * data-testid="rule-apply-banner-cta"
 * data-testid="rule-apply-banner-retry"
 */
import { useCallback, useEffect } from 'react';
import { Wand2, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAppDispatch, useAppSelector } from '@/features/hooks';
import {
  previewAllRules,
  clearRuleError,
} from '@/features/transactionRules/transactionRulesSlice';

interface RuleApplyBannerProps {
  /** Number of items in the uncategorized bucket (used to skip check if empty) */
  itemCount: number;
  /** Opens the preview modal */
  onOpenPreview: () => void;
}

// Session marker lifecycle:
//   absent    → not yet attempted (or transient failure cleared it)
//   'pending' → request in-flight; do NOT auto-retry, wait for settle
//   'true'    → fulfilled; auto-trigger suppressed until next session
const SESSION_CHECKED_KEY = 'fintrack:rule-banner-session-checked';
const SESSION_PENDING = 'pending';
const SESSION_DONE = 'true';

export function RuleApplyBanner({ itemCount, onOpenPreview }: RuleApplyBannerProps) {
  const dispatch = useAppDispatch();
  const rules = useAppSelector((s) => s.transactionRules.rules);
  const dryRunResult = useAppSelector((s) => s.transactionRules.dryRunResult);
  const isApplying = useAppSelector((s) => s.transactionRules.isApplying);
  const error = useAppSelector((s) => s.transactionRules.error);

  const activeRuleCount = rules.filter((r) => r.isActive).length;
  const hasRules = activeRuleCount > 0;
  const shouldCheck = hasRules && itemCount > 0;

  // Single, deterministic dispatch path. Marks the session as 'pending' before
  // the request fires (so re-renders do NOT auto-retry), and only commits
  // 'true' once the dryRun fulfils. A rejection removes the marker so the
  // next mount or explicit retry can re-fire.
  const kickOffDryRun = useCallback(() => {
    sessionStorage.setItem(SESSION_CHECKED_KEY, SESSION_PENDING);
    dispatch(previewAllRules())
      .unwrap()
      .then(() => {
        sessionStorage.setItem(SESSION_CHECKED_KEY, SESSION_DONE);
      })
      .catch(() => {
        // Transient failure: drop the marker so future mounts (or the explicit
        // retry button) can re-trigger the request. The error state below keeps
        // this UI informed instead of silently rendering nothing.
        sessionStorage.removeItem(SESSION_CHECKED_KEY);
      });
  }, [dispatch]);

  // Auto-trigger dryRun once per session when conditions are met.
  //
  // The `error !== null` guard is critical: when `previewAllRules` rejects,
  // the rejected reducer flips `isApplying: true -> false` and `.catch(removeItem)`
  // runs in the same microtask, so by the time this effect re-runs the
  // sessionStorage flag is already absent. Without an explicit gate on
  // `error`, the effect would re-dispatch and hide the visible error banner
  // in an infinite retry loop. The guard makes the error state sticky until
  // the user explicitly clears it (Retry dispatches `clearRuleError`).
  useEffect(() => {
    if (!shouldCheck) return;
    if (dryRunResult !== null) return; // already checked successfully
    if (isApplying) return; // already in-flight
    if (error !== null) return; // last attempt rejected; wait for explicit Retry
    // Once per session — avoid re-checking on every tab switch.
    // 'pending' also blocks re-entry while the previous request is still
    // settling (or about to settle into the error state).
    const sessionFlag = sessionStorage.getItem(SESSION_CHECKED_KEY);
    if (sessionFlag === SESSION_DONE || sessionFlag === SESSION_PENDING) return;
    kickOffDryRun();
  }, [shouldCheck, dryRunResult, isApplying, error, kickOffDryRun]);

  // Loading state: compact spinner while the check is in-flight
  if (isApplying && dryRunResult === null) {
    return (
      <div
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border bg-muted/40 text-sm text-muted-foreground"
        data-testid="rule-apply-banner"
        aria-busy="true"
      >
        <Loader2 className="size-4 animate-spin shrink-0" />
        <span>Checking automation rules…</span>
      </div>
    );
  }

  // Error state: visible retry path when the dryRun was rejected and no
  // result is in hand yet. Without this branch the component returned null
  // and silently suppressed future checks.
  if (error && !isApplying && dryRunResult === null) {
    return (
      <div
        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30"
        data-testid="rule-apply-banner"
        data-state="error"
        role="alert"
      >
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="size-4 text-amber-600 dark:text-amber-400 shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium text-amber-900 dark:text-amber-200 break-words">
            Couldn&apos;t check automation rules
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="min-h-[44px] min-w-[44px] shrink-0"
          onClick={() => {
            // Clear any stale error text before re-dispatching so the loading
            // branch takes over immediately. The pending marker prevents the
            // auto-effect from racing with the manual retry.
            dispatch(clearRuleError());
            kickOffDryRun();
          }}
          data-testid="rule-apply-banner-retry"
        >
          Retry
        </Button>
      </div>
    );
  }

  // No matches → hide banner entirely
  if (!dryRunResult || dryRunResult.appliedCount === 0) return null;

  const { appliedCount, hasMorePreview } = dryRunResult;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-violet-200 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30"
      data-testid="rule-apply-banner"
    >
      <div className="flex items-center gap-2 min-w-0">
        <Wand2 className="size-4 text-violet-600 dark:text-violet-400 shrink-0" />
        <p className="text-sm font-medium text-violet-900 dark:text-violet-200">
          {appliedCount}
          {hasMorePreview && '+'} transaction{appliedCount !== 1 ? 's' : ''} can be auto-categorized
          by your rules
        </p>
        <span className="text-xs text-violet-600 dark:text-violet-400 hidden sm:inline">
          · {activeRuleCount} active rule{activeRuleCount !== 1 ? 's' : ''}
        </span>
      </div>
      <Button
        size="sm"
        className="min-h-[36px] bg-violet-600 hover:bg-violet-700 text-white dark:bg-violet-700 dark:hover:bg-violet-600 shrink-0"
        onClick={onOpenPreview}
        data-testid="rule-apply-banner-cta"
      >
        <Wand2 className="size-3.5 mr-1.5" />
        Preview &amp; Apply
      </Button>
    </div>
  );
}